import {
  AuthorizationRelations,
  AuthorizationResource,
  AuthorizationResourcesAndSubjects
} from '@/types/authorization'

export type PlatformPermissions =
  | 'is_super_administrator'
  | 'super_adminstrator_accessing'
  | 'can_view_antecipe_affiliates'

export type CompanyPermissions =
  | 'access_level_editing_grant_access'
  | 'access_level_granting_grant_access'
  | 'access_level_removing_grant_access'
  | 'sharing_modal_reading_grant_access'
  | 'company_policy_links_editing'
  | 'company_policy_toggle_activation'
  | 'company_policy_creating'
  | 'export_reports'

export type HealthUnitPermissions =
  | 'health_unit_policy_links_editing'
  | 'health_unit_creating'
  | 'health_unit_editing'
  | 'health_unit_deactivating'
  | 'health_unit_basic_reading'
  | 'health_unit_reordering'

export type SchedulePermissions =
  | 'schedule_policy_links_editing'
  | 'schedule_payment_policies_creating'
  | 'schedule_payment_policies_editing'
  | 'schedule_payment_policies_deactivating'
  | 'schedule_formation_creating'
  | 'schedule_formation_editing'
  | 'schedule_formation_deactivating'
  | 'schedule_position_creating'
  | 'schedule_position_editing'
  | 'schedule_position_deactivating'
  | 'schedule_shift_creating'
  | 'schedule_shift_editing'
  | 'schedule_shift_deactivating'
  | 'schedule_creating'
  | 'schedule_editing'
  | 'schedule_deactivating'
  | 'station_exceptionalities_list_reading'
  | 'schedule_payment_policies_links_reading'
  | 'schedule_payment_policies_reading'
  | 'schedule_proportionality_rule_reading'
  | 'schedule_requeriment_reading'
  | 'schedule_basic_reading'
  | 'station_exceptionalities_reverting'
  | 'station_exceptionalities_approving'
  | 'station_exceptionalities_reproving'
  | 'station_observations_deleting'
  | 'station_observations_editing'
  | 'station_deleting'
  | 'station_checks_evidences_managing'
  | 'station_creating'
  | 'station_doctor_package_removing'
  | 'station_doctor_package_replacing'
  | 'station_doctor_package_allocating'
  | 'station_exceptionalities_cancelling'
  | 'station_exceptionalities_requesting'
  | 'station_observations_creating'
  | 'station_editing'
  | 'station_exceptionalities_reading'
  | 'station_observations_reading'
  | 'station_antecipe_reading'
  | 'station_payment_rules_reading'
  | 'station_edition_historical_reading'
  | 'station_reports_extraction'
  | 'station_doctor_check_reading'
  | 'station_biometry_photo_reading'
  | 'station_basic_reading'
  | 'doctor_document_reviewing'
  | 'doctor_document_reading'
  | 'post_station_panel_reading'
  | 'post_station_domain_detail_reading'
  | 'post_station_history_reading'
  | 'post_station_station_status_reading'
  | 'post_station_qualification_documental_reading'
  | 'post_station_qualification_transfer_reading'
  | 'post_station_approval_reverting'
  | 'post_station_manual_revalidating'

export type DsAttendancePermissions =
  | 'attendance_configuring'
  | 'attendance_emitting'
  | 'attendance_basic_receiving'
  | 'attendance_advanced_receiving'
  | 'attendance_extracting'
  | 'attendance_reading'
  | 'attendance_sheet_exporting'
  | 'attendance_general_administrating'
  | 'attendance_registration_configuring'

export type NpsPermissions =
  | 'nps_form_creating'
  | 'nps_form_editing'
  | 'nps_form_cloning'
  | 'nps_form_moving'
  | 'nps_evaluator_managing'
  | 'nps_settings_managing'
  | 'nps_audit_exporting'
  | 'nps_technical_evaluating'
  | 'nps_patient_nps_reading'
  | 'nps_institutional_evaluating'
  | 'nps_own_history_reading'
  | 'nps_dashboard_reading'
  | 'nps_ranking_exporting'

export type PostStationPermissions =
  | 'post_station_panel_reading'
  | 'post_station_approval_reverting'
  | 'post_station_manual_revalidating'
  | 'qualification_documental_policy_reading'
  | 'qualification_documental_policy_creating'
  | 'qualification_documental_policy_editing'
  | 'qualification_documental_policy_toggling'
  | 'qualification_documental_policy_linking'
  | 'qualification_documental_doctors_managing'
  | 'qualification_documental_bulk_importing'
  | 'qualification_transfer_policy_reading'
  | 'qualification_transfer_policy_creating'
  | 'qualification_transfer_policy_editing'
  | 'qualification_transfer_policy_toggling'
  | 'qualification_transfer_policy_linking'
  | 'qualification_transfer_doctors_managing'
  | 'qualification_transfer_bulk_importing'

export type AllPermissions =
  | PlatformPermissions
  | CompanyPermissions
  | HealthUnitPermissions
  | SchedulePermissions
  | DsAttendancePermissions
  | NpsPermissions
  | PostStationPermissions

export type PermissionSet = Record<string, Partial<Record<AllPermissions, boolean>>>
export type PermissionsObject = Record<string, PermissionSet>

export type AuthorizationRelationResponse = {
  id: number
  resource_type: AuthorizationResourcesAndSubjects
  resource_id: number
  resource_relation: AuthorizationRelations
  effective_relation?: AuthorizationRelations
  subject_type: AuthorizationResourcesAndSubjects
  subject_id: number
  subject_name: string
  subject_description?: string
  inheritances: {
    is_inherited: boolean
    resource_type: string
    resource_id: number
    resource_name?: string
    resource_relation: AuthorizationRelations
  }[]
}

export interface AuthorizationRelationPaginatedResponse {
  data: AuthorizationRelationResponse[]
  count: number
}

export interface InheritanceItem {
  is_inherited: boolean
  resource_type: string
  resource_id: number
  resource_relation: string
  resource_name?: string
}

export interface AuthorizationRelationResponseExpanded {
  id: number
  resource_type: AuthorizationResourcesAndSubjects
  resource_id: number
  resource_relation: AuthorizationRelations
  effective_relation: AuthorizationRelations
  subject_type: AuthorizationResourcesAndSubjects
  subject_id: number
  subject_name: string
  subject_description?: string
  inheritances?: InheritanceItem[]
  inheritance?: {
    is_inherited: boolean
    resource_type: string
    resource_id: number
  }
}

export const resourceHierarchyMap: Record<string, number> = {
  [AuthorizationResourcesAndSubjects.Company]: 1,
  [AuthorizationResourcesAndSubjects.HealthUnit]: 2,
  [AuthorizationResourcesAndSubjects.Schedule]: 3,
  [AuthorizationResourcesAndSubjects.NpsCompany]: 1,
  [AuthorizationResourcesAndSubjects.NpsHealthUnit]: 2,
  [AuthorizationResourcesAndSubjects.PostStationCompany]: 1,
  [AuthorizationResourcesAndSubjects.PostStationHealthUnit]: 2,
  [AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany]: 1,
  [AuthorizationResourcesAndSubjects.StationExceptionalitiesHealthUnit]: 2,
  [AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule]: 3
}

export const permissionPowerMap: Record<AuthorizationRelations, number> = {
  [AuthorizationRelations.BasicReader]: 1,
  [AuthorizationRelations.IntermediateReader]: 2,
  [AuthorizationRelations.AdvancedReader]: 3,
  [AuthorizationRelations.BasicEditor]: 4,
  [AuthorizationRelations.IntermediateEditor]: 5,
  [AuthorizationRelations.ContentAdmin]: 6,
  [AuthorizationRelations.GeneralAdmin]: 7,
  [AuthorizationRelations.Platform]: 99,
  [AuthorizationRelations.Parent]: 0,
  [AuthorizationRelations.DirectMember]: 0,
  [AuthorizationRelations.Member]: 0,
  [AuthorizationRelations.Extractor]: 1,
  [AuthorizationRelations.Emitter]: 2,
  [AuthorizationRelations.BasicReceiver]: 3,
  [AuthorizationRelations.AdvancedReceiver]: 4,
  [AuthorizationRelations.QualityAdmin]: 0,
  [AuthorizationRelations.TechnicalEvaluator]: 0,
  [AuthorizationRelations.InstitutionalEvaluator]: 0,
  [AuthorizationRelations.AnalyticsViewer]: 0,
  [AuthorizationRelations.Operator]: 0,
  [AuthorizationRelations.MedicalCoordinator]: 0,
  [AuthorizationRelations.AuditAnalyst]: 0,
  [AuthorizationRelations.TechnicalValidator]: 0,
  [AuthorizationRelations.StrategicValidator]: 0,
  [AuthorizationRelations.ExecutiveValidator]: 0,
  // Analistas do Pós-Plantão: peso 35 (mesma faixa do PERMISSION_HIERARCHY do backend).
  // Acima do topo da escala hierárquica genérica (GeneralAdmin=7) de propósito: mantém os perfis
  // de analista selecionáveis em getSelectablePermissions mesmo quando o subject herda um papel
  // hierárquico — com 0 eles ficariam desabilitados (0 <= poder herdado).
  [AuthorizationRelations.DocumentationAnalyst]: 35,
  [AuthorizationRelations.FinancialAnalyst]: 35
}

type AuthorizationRelationItem = {
  readAt: {
    token: string
  }
  relationship: {
    relation: AuthorizationRelations
    resource: AuthorizationResource
    subject: {
      optionalRelation: string
      object: AuthorizationResource
    }
  }
  afterResultCursor: {
    token: string
  }
}

export type AuthorizationRelationsResponse = AuthorizationRelationItem[]

export interface SubjectCandidateResponse {
  id: number
  name: string
  email?: string
  description?: string
  type: AuthorizationResourcesAndSubjects
  avatar?: string
}
